import axios, { AxiosInstance } from 'axios'
import configService from '@/services/configService'
import { get_jwt_token, isLoggedIn } from '@/services/auth'
import { ApplicationConfiguration } from '@/types'
import router from '@/router'
import { isAplosApiDiagnosticResponse } from '@/models/AplosApiResponse'
import { useUser } from '@/composables/useUser'
import { useErrorStore, type ErrorEntry } from '@/store/errorStore'
import type { MetricsQueryParams } from '@/models/MetricsSnapshot'



let api: AxiosInstance | null = null

export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
  OPTIONS = 'OPTIONS',
  HEAD = 'HEAD',
}

export interface IAplosApiRequestArgs {

  tenantId: string,
  userId: string,

}


const initializeApi = async (): Promise<AxiosInstance> => {
  if (!api) {
    await configService.loadApplicationConfig()
    const appConfig = configService.getApplicationConfigData() as ApplicationConfiguration

    if (!appConfig || !appConfig.api?.url) {
      throw new Error('API URL is not defined in the configuration')
    }

    const APLOS_DOMAIN = appConfig.api.url

    api = axios.create({ baseURL: APLOS_DOMAIN })

    api.interceptors.request.use(async (requestConfig) => {
      requestConfig.headers = requestConfig.headers ?? {}
      const jwt = await get_jwt_token()
      requestConfig.headers.Authorization = `Bearer ${jwt}`
      return requestConfig
    })

    api.interceptors.response.use(
      // Unwrap duplicate data property if exists and capture diagnostics where present
      (resp) => {
        if (resp?.data && isAplosApiDiagnosticResponse(resp.data)) {
          // preserve diagnostics on the response for callers if needed
          ; (resp as any).apiDiagnostics = resp.data.diagnostics
          resp.data = resp.data.data
        }
        return resp
      },
      async (error) => {
        // normalize error response body if API used an envelope and surface diagnostics
        const r = error?.response
        if (r?.data && isAplosApiDiagnosticResponse(r.data)) {
          // attach diagnostics to the thrown error for higher-level handlers / logging
          ; (error as any).apiDiagnostics = r.data.diagnostics

          // augment message with useful diagnostic fields (if present)
          const diagParts: string[] = []
          if (r.data.statusCode) diagParts.push(`statusCode=${r.data.statusCode}`)
          if (r.data.timestamp) diagParts.push(`timestamp=${r.data.timestamp}`)
          const d = r.data.diagnostics
          if (d) {
            if (d.startTime) diagParts.push(`start=${d.startTime}`)
            if (d.endTime) diagParts.push(`end=${d.endTime}`)
            if (d.duration) diagParts.push(`duration=${d.duration}`)
          }
          if (diagParts.length) {
            error.message = `${error.message} [api diagnostics: ${diagParts.join(', ')}]`
          }

          // unwrap payload so callers receive the inner data shape (consistent with successful path)
          r.data = r.data.data
          error.response = r
        }
        if (r && r.status === 401) {
          // Only redirect to login if the user's session is truly expired.
          // Transient backend 401s (e.g., misconfigured endpoints) should not
          // kick the user out of the app.
          const stillLoggedIn = await isLoggedIn()
          if (!stillLoggedIn) {
            router.replace('/')
          }
        }

        // Capture error for developer error viewer (role-gated)
        try {
          const { is_global_admin } = useUser()
          if (is_global_admin()) {
          const errorStore = useErrorStore()
            const entry: ErrorEntry = {
              id: crypto.randomUUID(),
              httpStatusCode: r?.status ?? 0,
              requestUrl: error.config?.url ?? 'unknown',
              httpMethod: (error.config?.method ?? 'unknown').toUpperCase(),
              errorMessage: error.message,
              responseBody: r?.data ?? null,
              capturedAt: new Date().toISOString(),
              apiDiagnostics: (error as any).apiDiagnostics
                ? {
                    statusCode: (error as any).apiDiagnostics.statusCode,
                    timestamp: (error as any).apiDiagnostics.timestamp,
                    startTime: (error as any).apiDiagnostics.startTime,
                    endTime: (error as any).apiDiagnostics.endTime,
                    duration: (error as any).apiDiagnostics.duration,
                  }
                : undefined,
            }
            errorStore.pushError(entry)
          }
        } catch {
          // Silently ignore — error viewer is non-critical
        }

        return Promise.reject(error)
      }
    )
  }

  return api // Ensure AxiosInstance is always returned
}

/**
 * Returns "/v3" or "" based on the api_routing config for the given domain.
 * Domains: "users" | "tenants" | "subscriptions" | "site_messages" | "metrics"
 * Falls back to "" (v1) if config is not loaded or domain is not found.
 */
export function getRoutePrefix(domain: string): string {
  const appConfig = configService.getApplicationConfigData()
  const version = appConfig?.api_routing?.[domain]
  const prefix = version === 'v1' ? '/v1' : '/v3';
  return prefix
}

export function getTenantPath(tenantId?: string) {
  if (!tenantId) {
    throw new Error("tenant id is required.")
  }

  return `${getRoutePrefix('tenants')}/tenants/${tenantId}`
}

export function getUserPath(tenantId?: string, userId?: string) {

  const prefix = getRoutePrefix('users')
  // When users is v3, use the v3 prefix directly (not the tenant prefix)
  const basePath = `${prefix}/tenants/${tenantId}/users`;
  return userId ? `${basePath}/${userId}` : basePath;

}

/**
 * Builds a query string from MetricsQueryParams.
 * Returns an empty string when no params are provided or all values are undefined.
 */
export function buildMetricsQueryString(params?: MetricsQueryParams): string {
  if (!params) return ''
  const parts: string[] = []
  if (params.startDate) parts.push(`start_date=${encodeURIComponent(params.startDate)}`)
  if (params.endDate) parts.push(`end_date=${encodeURIComponent(params.endDate)}`)
  if (params.days != null) parts.push(`days=${params.days}`)
  if (params.limit != null) parts.push(`limit=${params.limit}`)
  if (params.startKey) parts.push(`start_key=${encodeURIComponent(params.startKey)}`)
  if (params.tenantOnly != null) parts.push(`tenant_only=${params.tenantOnly}`)
  if (params.lifecycleState && params.lifecycleState !== 'all') {
    parts.push(`lifecycle_state=${encodeURIComponent(params.lifecycleState)}`)
  }
  return parts.length ? `?${parts.join('&')}` : ''
}

export const submitApiRequest = async (method: string, url: string, data?: any) => {
  const api = await initializeApi()
  const requestConfig = data ? { method, url, data } : { method, url }
  return api.request(requestConfig)
}

const aplosApi = {
  apiInstance: new Proxy<AxiosInstance>({} as AxiosInstance, {
    get: function (_, prop) {
      if (prop === 'then') {
        return api
      }
      return async (...args: any[]) => {
        const instance = await initializeApi()
        const method = (instance as any)[prop]
        if (typeof method === 'function') {
          return method.apply(instance, args)
        }
        return method
      }
    }
  }),

  // API methods
  executeAnalysis: async (
    tenantId?: string,
    userId?: string,
    fileId?: string,
    configs_json?: string,
    meta_data_json?: string,
    data_processing?: string | Record<string, any>,
    post_processing?: string | Record<string, any>
  ) => {

    if (!fileId) throw new Error('File ID is required')
    if (!configs_json) throw new Error('Configs JSON is required')

    let data_p: any = {}
    if (data_processing) {
      // check if it's a string
      if (typeof data_processing === 'string') {
        data_p = JSON.parse(data_processing)
      }
      if (typeof data_processing === 'object') {
        data_p = data_processing

      }
    }

    let post_p: any = {}
    if (post_processing) {
      // check if it's a string
      if (typeof post_processing === 'string') {
        post_p = JSON.parse(post_processing)
      }
      if (typeof post_processing === 'object') {
        post_p = post_processing

      }
    }

    const payload = {
      file: { id: fileId },
      configuration: configs_json,
      meta_data: meta_data_json,
      data_processing: {},
      post_processing: {}
    }
    if (data_p) {
      if (data_p.data_processing) {
        payload.data_processing = data_p.data_processing;
      }
      else if (data_p) {
        payload.data_processing = data_p;
      }
    }

    if (post_p) {
      if (post_p.post_processing) {
        payload.post_processing = post_p.post_processing;
      } else if (post_p) {
        payload.post_processing = post_p;
      }
    }

    const results = await submitApiRequest(
      'post',
      `${getUserPath(tenantId, userId)}/nca/executions`,
      payload
    )
    return results
  },

  getExecution: async (tenantId?: string, userId?: string, executionId?: string) => {
    return await aplosApi.getExecutionStatus(tenantId, userId, executionId)
  },

  getExecutionStatus: async (tenantId?: string, userId?: string, executionId?: string) => {
    return await submitApiRequest(
      'get',
      `${getUserPath(tenantId, userId)}/nca/executions/${executionId}`
    )
  },

  getExecutionPreProcessRules: async (tenantId?: string, userId?: string, executionId?: string) => {
    return await submitApiRequest(
      'get',
      `${getUserPath(tenantId, userId)}/nca/executions/${executionId}/data-processing`
    )
  },

  getExecutionPostProcessRules: async (tenantId?: string, userId?: string, executionId?: string) => {
    return await submitApiRequest(
      'get',
      `${getUserPath(tenantId, userId)}/nca/executions/${executionId}/post-processing`
    )
  },

  getFileHeaders: async (tenantId?: string, userId?: string, fileId?: string) => {
    return await submitApiRequest(
      'get',
      `${getUserPath(tenantId, userId)}/nca/files/${fileId}`
    )
  },

  getExecutions: async (tenantId?: string, userId?: string, limit?: number, filters: string | null = null, lastKey?: string, start_date?: string, end_date?: string) => {
    const auth = useUser();
    await auth.initialize();
    limit = limit || auth.userSettings.execution_response_limit
    filters = filters ? `?${filters}` : `?limit=${limit}`;
    if (lastKey) {
      const b64 = btoa(JSON.stringify(lastKey));
      filters += `&last-key=${b64}`;
    }
    if (start_date) {
      filters += `&start-date=${start_date}`;
    }
    if (end_date) {
      filters += `&end-date=${end_date}`;
    }
    const path = `${getUserPath(tenantId, userId)}/nca/executions${filters}`;

    const results = await submitApiRequest('get', path)
    return results
  },


  getUser: async (tenantId?: string, userId?: string) => {
    return await submitApiRequest('get', `${getUserPath(tenantId, userId)}`)
  },

  getUserMetrics: async (tenantId?: string, userId?: string) => {
    const prefix = getRoutePrefix('metrics')
    return await submitApiRequest('get', `${prefix}/tenants/${tenantId}/users/${userId}/metrics`)
  },

  updateUser: async (tenantId?: string, userId?: string, data?: any) => {
    return await submitApiRequest('post', `${getUserPath(tenantId, userId)}`, data)
  },

  getTenants: async (tenantId?: string, filter?: string) => {
    const prefix = getRoutePrefix('tenants')
    return await submitApiRequest('get', tenantId ? `${prefix}/tenants/${tenantId}` : `${prefix}/tenants?status=${filter}`)
  },

  getTenant: async (tenantId?: string, filter?: string) => {
    const prefix = getRoutePrefix('tenants')
    return await submitApiRequest('get', tenantId ? `${prefix}/tenants/${tenantId}` : `${prefix}/tenants?status=${filter}`)
  },

  getTenantUsers: async (tenantId?: string) => {
    return await submitApiRequest('get', `${getTenantPath(tenantId)}/users`)
  },

  getTenantsSubscriptionList: async (tenantId?: string) => {
    const prefix = getRoutePrefix('subscriptions')
    return await submitApiRequest('get', `${prefix}/tenants/${tenantId}/subscriptions`)
  },

  getActiveSubscription: async (tenantId: string) => {
    const prefix = getRoutePrefix('subscriptions')
    return await submitApiRequest('get', `${prefix}/tenants/${tenantId}/active-subscription`)
  },

  getSubscriptionStatus: async (tenantId: string) => {
    const prefix = getRoutePrefix('subscriptions')
    return await submitApiRequest('get', `${prefix}/tenants/${tenantId}/subscription-status`)
  },

  validateSubscription: async (tenantId: string, profileCount?: number) => {
    const prefix = getRoutePrefix('subscriptions')
    const qs = profileCount != null ? `?profile_count=${profileCount}` : ''
    return await submitApiRequest('get', `${prefix}/tenants/${tenantId}/validate-subscription${qs}`)
  },

  updateTenant: async (data?: any, tenantId?: any) => {
    const result = await submitApiRequest('post', `${getTenantPath(tenantId)}`, data);
    return result;
  },

  createTenant: async (data?: any) => {
    const prefix = getRoutePrefix('tenants')
    const result = await submitApiRequest('post', `${prefix}/tenants/`, data);
    return result;
  },

  updateSubscription: async (tenantId?: string, data?: any) => {
    const prefix = getRoutePrefix('subscriptions')
    return await submitApiRequest('post', `${prefix}/tenants/${tenantId}/subscriptions/${data?.subscription?.id ?? ""}`, data)
  },

  getAssignableRoles: async (tenantId?: string, userId?: string) => {
    const result = await submitApiRequest('get', `${getUserPath(tenantId, userId)}/assignable-roles`,);
    return result;
  },

  getPublicOutMessages: async () => {
    const prefix = getRoutePrefix('site_messages')
    return await submitApiRequest('get', `${prefix}/app/messages`)
  },

  getUserMessages: async (tenantId?: string, userId?: string) => {
    const prefix = getRoutePrefix('site_messages')
    return await submitApiRequest('get', `${prefix}/tenants/${tenantId}/users/${userId}/messages`)
  },

  getTenantMetrics: async (tenantId?: string) => {
    const prefix = getRoutePrefix('metrics')
    return await submitApiRequest('get', `${prefix}/tenants/${tenantId}/metrics`)
  },

  getMetricsSnapshots: async (params?: MetricsQueryParams) => {
    const prefix = getRoutePrefix('metrics')
    const qs = buildMetricsQueryString(params)
    return await submitApiRequest('get', `${prefix}/tenants/*/metrics${qs}`)
  },

  getTenantMetricsSnapshots: async (tenantId: string, params?: MetricsQueryParams) => {
    const prefix = getRoutePrefix('metrics')
    const qs = buildMetricsQueryString(params)
    return await submitApiRequest('get', `${prefix}/tenants/${tenantId}/metrics${qs}`)
  },

  getUserMetricsSnapshots: async (tenantId: string, userId: string, params?: MetricsQueryParams) => {
    const prefix = getRoutePrefix('metrics')
    const qs = buildMetricsQueryString(params)
    return await submitApiRequest('get', `${prefix}/tenants/${tenantId}/users/${userId}/metrics${qs}`)
  },

  getDrilldownExecutions: async (tenantId: string, userId?: string, params?: { status?: string; limit?: number; startKey?: string }) => {
    const prefix = getRoutePrefix('metrics')
    const parts: string[] = []
    if (params?.status) parts.push(`status=${encodeURIComponent(params.status)}`)
    if (params?.limit) parts.push(`limit=${params.limit}`)
    if (params?.startKey) parts.push(`start_key=${encodeURIComponent(params.startKey)}`)
    const qs = parts.length ? `?${parts.join('&')}` : ''
    const userSegment = userId ? `/users/${userId}` : ''
    return await submitApiRequest('get', `${prefix}/tenants/${tenantId}${userSegment}/metrics/executions${qs}`)
  },


  deleteFile: async (tenantId?: string, userId?: string, fileId?: string) => {
    return await submitApiRequest('post', `${getUserPath(tenantId, userId)}/nca/files/${fileId}/delete`)
  }
};

export default aplosApi
